import { QUIET_HOURS_SMS_DISCLAIMER } from 'modules/campaigns/lib/constants';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { Link } from 'ui/links';
import { RadioListField, TimePickerField } from 'lib/iterable-hook-form/FormField';
import { HelpToggletip } from 'ui/tooltip';
import { ErrorText, OptionText } from '../../../styles';
import { CampaignEditorFormState } from '../../../types';
import { ExpandableToggle } from './ExpandableToggle';
import { Caption, ExpandableToggleContent, FormBodyText } from './OptimizeSection.styles';

const SEND_SKIP_OPTIONS = [
  {
    id: 'no-send-skip',
    name: () => <OptionText>Send the message after quiet hours ends</OptionText>,
    value: false,
  },
  {
    id: 'send-skip',
    name: () => <OptionText>Cancel the send</OptionText>,
    value: true,
  },
];

const SendSkip = styled.div`
  margin-top: ${spacing.base};
`;

type EditableQuietHoursProps = {
  /** Whether campaign medium is SMS */
  isSMSCampaign?: boolean;
};

const QuietHoursCaption = ({ isSMSCampaign }: { isSMSCampaign?: boolean }) => (
  <>
    Prevents messages from being sent during part of the day.&nbsp;
    {isSMSCampaign && QUIET_HOURS_SMS_DISCLAIMER}
    &nbsp;
    <Link
      size="caption"
      hasOpenNewTabIcon
      href="https://support.iterable.com/hc/articles/7412316462996#quiet-hours"
      rel="noreferrer noopener"
      target="_blank"
    >
      Learn more
    </Link>
  </>
);

const validationKey = 'customQuietHours';

export const EditableQuietHours = ({ isSMSCampaign }: EditableQuietHoursProps) => {
  const {
    watch,
    trigger,
    resetField,
    formState: { errors },
  } = useFormContext<CampaignEditorFormState>();

  useEffect(() => {
    watch(({ useQuietHours }, { name }) => {
      if (name === 'useQuietHours' && useQuietHours === false) {
        resetField(validationKey);
        trigger(validationKey);
      }
      if (name?.startsWith(validationKey)) {
        trigger(validationKey);
      }
    });
  }, [watch, trigger]);

  const { customQuietHours: customQuietHoursError } = errors;

  return (
    <ExpandableToggle
      name="useQuietHours"
      tooltipContent={QUIET_HOURS_SMS_DISCLAIMER}
      caption={<QuietHoursCaption isSMSCampaign={isSMSCampaign} />}
    >
      <ExpandableToggleContent>
        <FormBodyText>Between</FormBodyText>
        <TimePickerField
          name="customQuietHours.startTime"
          data-testid={`campaign-form-quietHours-startTime`}
          increment={30}
          inputClockFormat="24-hour"
        />
        and
        <TimePickerField
          name="customQuietHours.endTime"
          data-testid={`campaign-form-quietHours-endTime`}
          increment={30}
          inputClockFormat="24-hour"
        />
        <HelpToggletip
          tooltipContent="User’s local time zone is used if available. Project time zone is used as a fallback."
          iconProps={{ color: colors.secondaryText }}
        />
        {customQuietHoursError?.message && <ErrorText>{customQuietHoursError.message}</ErrorText>}
        <SendSkip>
          <Caption>If a message is triggered during quiet hours:</Caption>
          <RadioListField name="customQuietHours.sendSkip" options={SEND_SKIP_OPTIONS} />
        </SendSkip>
      </ExpandableToggleContent>
    </ExpandableToggle>
  );
};
